<template>
  <div class="converged-workbench">
    <workbench-canvas
      :copiedTarget="copiedTarget"
      :viewOnly="viewOnly"
    >
      <template
        slot="main-container"
        slot-scope="workbenchSlottedValue"
      >
        <template v-if="noSelectedDatasetFamily">
          <div
            class="text-h4 text-md-center"
            style="margin-top: 20vh"
          >
            <p>Graphs will be available when you select at least one attribute</p>
          </div>
        </template>
        <template v-else-if="showInsights">
          <insights
            :workbenchSlottedValue="workbenchSlottedValue.slottedValue"
            :viewOnly="viewOnly"
          />
        </template>
        <template v-else-if="selectedLasDatasetFamily">
          <las-graphs :workbenchSlottedValue="workbenchSlottedValue.slottedValue" />
        </template>
      </template>
      <template
        slot="bottom-pane"
        slot-scope="bottomPane"
      >
        <bottom-panel
          :targetData="bottomPane.copiedTarget"
          :viewOnly="viewOnly"
          :xandrActions="noSelectedDatasetFamily || showInsights"
          :lasActions="selectedLasDatasetFamily"
        />
      </template>
    </workbench-canvas>
    <save-workbench-changes-modal
      ref="saveWorkbenchChangesModal"
      :newXandrTarget="copiedTarget"
    />
  </div>
</template>

<script>
  import WorkbenchMixin from './mixin'
  import Insights from '@/appComponents/workbench/insights/Insights'
  import LasGraphs from '@/appComponents/workbench/lasGraphs/LasGraphs.vue'
  import BottomPanel from '@/appComponents/workbench/bottomPanel/BottomPanel'
  import SaveWorkbenchChangesModal from '@/appComponents/workbench/saveChanges/SaveWorkbenchChangesModal.vue'
  import { cloneDeep } from 'lodash'
  import {
    ACTIVATION_STATUS,
    DESTINATIONS,
    TARGET_TYPE,
    TARGET_TYPES_INTEGERS,
    DATASOURCES,
  } from '@/appLogic/targets/Constants'
  import { SET_CURRENT_TARGET, DELETE_CURRENT_TARGET } from '@/appLogic/targets/currentTarget/store/actions'
  import { SET_MVPDS } from '@/appLogic/targets/createTarget/store/actions'
  import { deleteTarget } from '@/api'

  export default {
    components: {
      Insights,
      LasGraphs,
      BottomPanel,
      SaveWorkbenchChangesModal,
    },
    mixins: [WorkbenchMixin],
    data() {
      return {
        interface: null,
        copiedTarget: undefined,
        availableDestinations: [],
      }
    },
    computed: {
      advertiser() {
        return this.originalTarget ? this.originalTarget.advertiser : null
      },
      targetName() {
        return this.originalTarget ? this.originalTarget.display_name : ''
      },
      selectedLasDatasetFamily() {
        let df = this.selectedDatasetFamily
        return df === 'amrld' || df === 'mri' || df === 'ncs'
      },
      selectedDatasetFamily() {
        return this.$store.getters['workbench/selectedFamilyDataset']
      },
      noSelectedDatasetFamily() {
        return !this.selectedDatasetFamily
      },
      selectedAdstraDatasetFamily() {
        return (
          this.selectedDatasetFamily === 'xandr' ||
          this.selectedDatasetFamily === 'adstra' ||
          this.selectedDatasetFamily === 'byod'
        )
      },
      selectedPrismDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.PRISM
      },
      selectedMaxDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.MAX
      },
      selectedDNBDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.DNB
      },
      selectedTVEDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.TVE
      },
      selectedEpsilonDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.EPSILON
      },
      selectedDiscoveryPlusDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.DISCOVERY_PLUS
      },
      selectedDCRDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.DCR
      },
      showInsights() {
        return (
          this.selectedAdstraDatasetFamily ||
          this.selectedPrismDatasetFamily ||
          this.selectedDNBDatasetFamily ||
          this.selectedMaxDatasetFamily ||
          this.selectedTVEDatasetFamily ||
          this.selectedEpsilonDatasetFamily ||
          this.selectedDiscoveryPlusDatasetFamily ||
          this.selectedDCRDatasetFamily
        )
      },
      as() {
        return this.originalTarget && this.originalTarget.activation_status
          ? this.originalTarget.activation_status
          : undefined
      },
      viewOnly() {
        let validStatus =
          [ACTIVATION_STATUS.WORKING, ACTIVATION_STATUS.DRAFT, ACTIVATION_STATUS.FAILED].indexOf(this.as) < 0
        const isModeled = this.isModeling || this.isModeled || this.isModeledFailed
        const isLASAndFailed = this.selectedLasDatasetFamily && this.as === ACTIVATION_STATUS.FAILED
        return (
          !this.hasPermissions ||
          this.isCancelled ||
          isModeled ||
          validStatus ||
          isLASAndFailed ||
          !this.$store.getters.hasCreateEditPermissions
        )
      },
      originalTarget() {
        let wt = this.$store.state.workbench.workbenchTarget
        return wt === null ? undefined : wt
      },
      isSubmitted() {
        return this.as === ACTIVATION_STATUS.SUBMITTED
      },
      isFailed() {
        return this.as === ACTIVATION_STATUS.FAILED
      },
      isLive() {
        return this.as === ACTIVATION_STATUS.LIVE
      },
      isModeling() {
        return this.as === ACTIVATION_STATUS.MODELING
      },
      isModeled() {
        return this.as === ACTIVATION_STATUS.MODELED
      },
      isCancelled() {
        return (
          this.originalTarget &&
          this.originalTarget.activation_status &&
          this.originalTarget.activation_status === ACTIVATION_STATUS.CANCELLED
        )
      },
      isSDTarget() {
        return (
          this.originalTarget &&
          this.originalTarget.destinations &&
          (this.originalTarget.destinations.includes(DESTINATIONS.KRUX) ||
            this.originalTarget.destinations.includes(DESTINATIONS.LIVERAMP))
        )
      },
      hasPermissions() {
        if (this.originalTarget && this.originalTarget.destinations) {
          return (this.originalTarget.destinations || []).every(destination => {
            switch (destination) {
              case DESTINATIONS.KRUX:
              case DESTINATIONS.LIVERAMP:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.SYMPHONY:
              case DESTINATIONS.FORESITE:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.DIRECTV:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.VIZIO:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.CANOE_LINEAR:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.CANOE_VOD:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.FREEWHEEL:
                return this.$store.getters.hasCreateEditPermissions
              case DESTINATIONS.GOOGLE_AD_MANAGER:
                return this.$store.getters.hasCreateEditPermissions
              default:
                return false
            }
          })
        } else {
          return false
        }
      },
    },
    mounted() {
      this.$store.dispatch(SET_MVPDS)
      this.copyTarget()
    },
    destroyed() {
      this.copiedTarget = undefined
    },
    beforeRouteLeave(to, from, next) {
      if (this.copiedTarget) {
        if (this.copiedTarget.activation_status === ACTIVATION_STATUS.DRAFT) {
          next()
          deleteTarget(this.copiedTarget)
            .then(function (response) {
              console.log(response)
            })
            .catch(function (err) {
              console.log(err)
            })
            .finally(() => {
              return
            })
        } else if (
          this.copiedTarget.activation_status === ACTIVATION_STATUS.LIVE ||
          this.copiedTarget.activation_status === ACTIVATION_STATUS.FAILED ||
          !this.$store.getters['workbench/saveXandrSegmentEnabled']
        ) {
          next()
          return
        } else {
          this.$refs.saveWorkbenchChangesModal.open(next)
        }
      } else {
        next()
      }
    },
    methods: {
      copyTarget() {
        this.copiedTarget = cloneDeep(
          this.originalTarget === null || !this.originalTarget ? { advertiser: { Name: null } } : this.originalTarget,
        )
        if (this.copiedTarget && !this.copiedTarget.advertiser) {
          this.copiedTarget.advertiser = { Name: null }
        }
        if (!this.copiedTarget.destinations) {
          this.copiedTarget.destinations = []
        }
        if (this.showInsights && !this.copiedTarget.xandr_data) {
          this.copiedTarget.xandr_data = { frequency: null }
        }

        const datasetFamilyTargetValues = [
          {
            selected: this.selectedAdstraDatasetFamily,
            target: TARGET_TYPE.ADSTRA,
            target_type: TARGET_TYPES_INTEGERS.ADSTRA,
          },
          {
            selected: this.selectedPrismDatasetFamily,
            target: TARGET_TYPE.PRISM,
            target_type: TARGET_TYPES_INTEGERS.PRISM,
          },
          { selected: this.selectedDNBDatasetFamily, target: TARGET_TYPE.DNB, target_type: TARGET_TYPES_INTEGERS.DNB },
          { selected: this.selectedMaxDatasetFamily, target: TARGET_TYPE.MAX, target_type: TARGET_TYPES_INTEGERS.MAX },
          {
            selected: this.selectedTVEDatasetFamily,
            target: TARGET_TYPE.TVE,
            target_type: TARGET_TYPES_INTEGERS.TVE,
          },
          {
            selected: this.selectedDiscoveryPlusDatasetFamily,
            target: TARGET_TYPE.DISCOVERY_PLUS,
            target_type: TARGET_TYPES_INTEGERS.DISCOVERY_PLUS,
          },
          {
            selected: this.selectedDCRDatasetFamily,
            target: TARGET_TYPE.DCR,
            target_type: TARGET_TYPES_INTEGERS.DCR,
          },
          { selected: this.selectedLasDatasetFamily, target: TARGET_TYPE.LAS, target_type: TARGET_TYPES_INTEGERS.LAS },
          {
            selected: this.selectedEpsilonDatasetFamily,
            target: TARGET_TYPE.EPSILON,
            target_type: TARGET_TYPES_INTEGERS.EPSILON,
          },
        ]
        const selectedFamily = datasetFamilyTargetValues.find(family => family.selected)
        if (selectedFamily) {
          this.copiedTarget.target = selectedFamily.target
          this.copiedTarget.target_type = selectedFamily.target_type
        }
      },
      deleteTarget() {
        this.$store.dispatch(SET_CURRENT_TARGET, cloneDeep(this.copiedTarget))
        this.$store.dispatch(DELETE_CURRENT_TARGET)
      },
      setViewOnly() {
        // TODO_AFTER
        // Since all of these are pertaining to the Xandr View only we might have to handle this in the store instead, through the interface
        // since a lot of the model has changed we will need to change how the target is handled in the UI
        // I will be setting the view only to false for now and we can come back to it later
        return false
        // const isLASAndLiveOrFailed = !this.isSDTarget && (this.isFailed || this.isLive)
        // const isModeled = this.isModeling || this.isModeled || this.isModeledFailed
        // this.viewOnly = this.isPendingApproval || this.isApproved || this.isSubmitted || !this.hasPermissions || isLASAndLiveOrFailed || this.isCancelled || isModeled
      },
    },
    watch: {
      originalTarget() {
        this.copyTarget()
        this.setViewOnly()
      },
      hasPermissions() {
        this.setViewOnly()
      },
    },
  }
</script>

<style>
  .converged-workbench {
    height: 100%;
  }
</style>



// WEBPACK FOOTER //
// WorkbenchConvergence.vue?02ef725e